
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DividendsAdjustmentCommonParams struct
 */
class DividendsAdjustmentCommonParams {
  /**
   * Initialize struct
   * @param {!string=} symbolName
   * @param {!Set=} groupNamePatterns
   * @param {!number=} longPoints
   * @param {!number=} shortPoints
   * @constructor
   */
  constructor (symbolName = '', groupNamePatterns = new Set(), longPoints = 0.0, shortPoints = 0.0) {
    this.SymbolName = symbolName
    this.GroupNamePatterns = groupNamePatterns
    this.LongPoints = longPoints
    this.ShortPoints = shortPoints
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DividendsAdjustmentCommonParams}
   * @param {!DividendsAdjustmentCommonParams} other Other struct
   * @returns {!DividendsAdjustmentCommonParams} This struct
   */
  copy (other) {
    if (other.SymbolName != null) {
      this.SymbolName = other.SymbolName
    } else {
      this.SymbolName = null
    }
    if (other.GroupNamePatterns != null) {
      this.GroupNamePatterns = new Set()
      for (let item of other.GroupNamePatterns) {
        if (item != null) {
          let tempItem
          tempItem = item
          this.GroupNamePatterns.add(tempItem)
        } else {
          this.GroupNamePatterns.add(null)
        }
      }
    } else {
      this.GroupNamePatterns = null
    }
    if (other.LongPoints != null) {
      this.LongPoints = other.LongPoints
    } else {
      this.LongPoints = null
    }
    if (other.ShortPoints != null) {
      this.ShortPoints = other.ShortPoints
    } else {
      this.ShortPoints = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DividendsAdjustmentCommonParams}
   * @returns {!DividendsAdjustmentCommonParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DividendsAdjustmentCommonParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DividendsAdjustmentCommonParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DividendsAdjustmentCommonParams}
   * @param {!DividendsAdjustmentCommonParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DividendsAdjustmentCommonParams)) {
      throw new TypeError('Instance of DividendsAdjustmentCommonParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DividendsAdjustmentCommonParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      SymbolName: ((this.SymbolName != null) ? this.SymbolName : null),
      GroupNamePatterns: ((this.GroupNamePatterns != null) ? Array.from(this.GroupNamePatterns, item => ((item != null) ? item : null)) : null),
      LongPoints: ((this.LongPoints != null) ? this.LongPoints : null),
      ShortPoints: ((this.ShortPoints != null) ? this.ShortPoints : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DividendsAdjustmentCommonParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DividendsAdjustmentCommonParams} other Object value
   * @returns {!DividendsAdjustmentCommonParams} Created struct
   */
  static fromObject (other) {
    return new DividendsAdjustmentCommonParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DividendsAdjustmentCommonParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DividendsAdjustmentCommonParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DividendsAdjustmentCommonParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6613
  }
}

export { DividendsAdjustmentCommonParams };

/**
 * Fast Binary Encoding DividendsAdjustmentCommonParams field model
 */
class FieldModelDividendsAdjustmentCommonParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.SymbolName = new fbe.FieldModelString(buffer, 4 + 4)
    this.GroupNamePatterns = new fbe.FieldModelSet(new fbe.FieldModelString(buffer, this.SymbolName.fbeOffset + this.SymbolName.fbeSize), buffer, this.SymbolName.fbeOffset + this.SymbolName.fbeSize)
    this.LongPoints = new fbe.FieldModelDouble(buffer, this.GroupNamePatterns.fbeOffset + this.GroupNamePatterns.fbeSize)
    this.ShortPoints = new fbe.FieldModelDouble(buffer, this.LongPoints.fbeOffset + this.LongPoints.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.SymbolName.fbeSize + this.GroupNamePatterns.fbeSize + this.LongPoints.fbeSize + this.ShortPoints.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.SymbolName.fbeExtra + this.GroupNamePatterns.fbeExtra + this.LongPoints.fbeExtra + this.ShortPoints.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDividendsAdjustmentCommonParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6613
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SymbolName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolName.fbeSize

    if ((fbeCurrentSize + this.GroupNamePatterns.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.GroupNamePatterns.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.GroupNamePatterns.fbeSize

    if ((fbeCurrentSize + this.LongPoints.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LongPoints.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LongPoints.fbeSize

    if ((fbeCurrentSize + this.ShortPoints.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ShortPoints.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ShortPoints.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @param {!DividendsAdjustmentCommonParams} fbeValue Default value, defaults is new DividendsAdjustmentCommonParams()
   * @returns {!DividendsAdjustmentCommonParams} DividendsAdjustmentCommonParams value
   */
  get (fbeValue = new DividendsAdjustmentCommonParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @param {!DividendsAdjustmentCommonParams} fbeValue DividendsAdjustmentCommonParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.SymbolName.fbeSize) <= fbeStructSize) {
      fbeValue.SymbolName = this.SymbolName.get()
    } else {
      fbeValue.SymbolName = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SymbolName.fbeSize

    if ((fbeCurrentSize + this.GroupNamePatterns.fbeSize) <= fbeStructSize) {
      this.GroupNamePatterns.get(fbeValue.GroupNamePatterns)
    } else {
      fbeValue.GroupNamePatterns.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.GroupNamePatterns.fbeSize

    if ((fbeCurrentSize + this.LongPoints.fbeSize) <= fbeStructSize) {
      fbeValue.LongPoints = this.LongPoints.get()
    } else {
      fbeValue.LongPoints = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LongPoints.fbeSize

    if ((fbeCurrentSize + this.ShortPoints.fbeSize) <= fbeStructSize) {
      fbeValue.ShortPoints = this.ShortPoints.get()
    } else {
      fbeValue.ShortPoints = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ShortPoints.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @param {!DividendsAdjustmentCommonParams} fbeValue DividendsAdjustmentCommonParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDividendsAdjustmentCommonParams}
   * @param {DividendsAdjustmentCommonParams} fbeValue DividendsAdjustmentCommonParams value
   */
  setFields (fbeValue) {
    this.SymbolName.set(fbeValue.SymbolName)
    this.GroupNamePatterns.set(fbeValue.GroupNamePatterns)
    this.LongPoints.set(fbeValue.LongPoints)
    this.ShortPoints.set(fbeValue.ShortPoints)
  }
}

export { FieldModelDividendsAdjustmentCommonParams };

/**
 * Fast Binary Encoding DividendsAdjustmentCommonParams model
 */
class DividendsAdjustmentCommonParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDividendsAdjustmentCommonParams(this.buffer, 4)
  }

  /**
   * Get the DividendsAdjustmentCommonParams model
   * @this {!DividendsAdjustmentCommonParamsModel}
   * @returns {!FieldModelDividendsAdjustmentCommonParams} model DividendsAdjustmentCommonParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DividendsAdjustmentCommonParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DividendsAdjustmentCommonParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DividendsAdjustmentCommonParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DividendsAdjustmentCommonParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDividendsAdjustmentCommonParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DividendsAdjustmentCommonParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DividendsAdjustmentCommonParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DividendsAdjustmentCommonParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DividendsAdjustmentCommonParamsModel}
   * @param {!DividendsAdjustmentCommonParams} value DividendsAdjustmentCommonParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DividendsAdjustmentCommonParamsModel}
   * @param {!DividendsAdjustmentCommonParams} value DividendsAdjustmentCommonParams value, defaults is new DividendsAdjustmentCommonParams()
   * @return {!object} Deserialized DividendsAdjustmentCommonParams value and its size
   */
  deserialize (value = new DividendsAdjustmentCommonParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DividendsAdjustmentCommonParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DividendsAdjustmentCommonParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DividendsAdjustmentCommonParamsModel}
   * @param {!number} prev Previous DividendsAdjustmentCommonParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DividendsAdjustmentCommonParamsModel };
